/** @format */

import { ActivatedRoute, Data, NavigationEnd, Params, Router, RouterModule } from '@angular/router';
import {
	BundleProductDataForAnalytics,
	ProductDataForAnalytics,
	ProductDetailsByUrlResponse
} from '@pdp/data-access/interfaces';
import {
	CartService,
	CookieService,
	EmitEvent,
	EventBusService,
	Events,
	HeaderLocationService,
	HttpService,
	KillswitchService,
	Store,
	StoreService,
	UtilityService,
	VirtualHostCookieService,
	WINDOW
} from '@shared/data-access/services';
import {
	Chain,
	Customer,
	Environment,
	HeaderStoreSelection,
	ISkuLocation,
	ProductsData,
	PromotionDetailsV1,
	Shipping
} from '@shared/data-access/interfaces';
import {
	Component,
	DestroyRef,
	NgZone,
	OnDestroy,
	OnInit,
	PLATFORM_ID,
	Renderer2,
	computed,
	inject,
	signal
} from '@angular/core';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Observable, Subject, firstValueFrom } from 'rxjs';
import {
	changeHeaderServiceStoreInfo,
	setCommonData,
	setCommonDsgVhHeader,
	setHeaderServiceStoreInfo,
	setUserStoreName,
	setUserZipCode
} from '@state/data-access';
import {
	fetchSDDInfo,
	modernCheckoutCookieChecked,
	selectBopisInventory,
	selectEcommInventory,
	selectKillswitch,
	selectPromotionData,
	selectResolvedSku,
	selectTemplateName,
	windowFlagsEventKillswitchesSet
} from '@pdp/data-access/state';
import { filter, map, mergeMap, take } from 'rxjs/operators';

import { AnalyticsPromotion } from './analytics-promotion.interface';
import { AuthenticationService } from '@shared/authentication/data-access';
import { CeopsComponent } from '@shared/ceops';
import { DynamicProductTemplate } from '@pdp/data-access/enums';
import { FeedbackComponent } from '@pdp/components';
import { HomrDataService } from '@shared/homr/data-access';
import { LoyaltyBalance } from '@scorecard/data-access';
import { MetaService } from '@seo/data-access';
import { Store as NgRxStore } from '@ngrx/store';
import { ProductAnalyticsService } from '@shared/data-access/analytics';
import { ProductSelectionService } from '@pdp/data-access/services';
import { ProductSelectionStateService } from '@plp/data-access/services';
import { ProductService } from '@shared/data-access/product';
import { adeptmindScriptPath } from './app.constants';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

declare const BUILD_VERSION: string;

const IMPORTS = [CeopsComponent, FeedbackComponent, RouterModule];

@Component({
	selector: 'pdp-root',
	templateUrl: './app.component.html',
	styleUrl: './app.component.scss',
	imports: [...IMPORTS],
	host: {
		'(window:HeaderServices:StoreSelection:OnLoad)': 'setStoreDetails($event)',
		'(window:HeaderServices:StoreSelection:Change)': 'updateStoreDetails($event)',
		'(window:HeaderServices:SameDayDelivery:Change)': 'handleDeliveryZip($event)',
		'(window:HeaderServices:SameDayDelivery:OnLoad)': 'handleDeliveryZip($event)',
		'(document:WindowFlagsEvent)': 'handleWindowFlagsEvent()'
	}
})
export class AppComponent implements OnInit, OnDestroy {
	private readonly document = inject<Document>(DOCUMENT);
	private readonly platformId = inject(PLATFORM_ID);
	private readonly window = inject<Window>(WINDOW);
	private readonly activatedRoute = inject(ActivatedRoute);
	private readonly analyticsService = inject(ProductAnalyticsService);
	private readonly authService = inject(AuthenticationService);
	private readonly cartService = inject(CartService);
	private readonly cookieService = inject(CookieService);
	private readonly environment = inject(Environment);
	private readonly eventBusService = inject(EventBusService);
	private readonly headerLocationService = inject(HeaderLocationService);
	private readonly homrDataService = inject(HomrDataService);
	private readonly httpService = inject(HttpService);
	private readonly killSwitchService = inject(KillswitchService);
	private readonly metaService = inject(MetaService);
	private readonly ngrxStore = inject(NgRxStore);
	private readonly productService = inject(ProductService);
	private readonly productSelectionService = inject(ProductSelectionService);
	private readonly productSelectionStateService = inject(ProductSelectionStateService);
	private readonly renderer = inject(Renderer2);
	private readonly router = inject(Router);
	private readonly store = inject(Store);
	private readonly storeService = inject(StoreService);
	private readonly utilityService = inject(UtilityService);
	private readonly virtualHostCookieService = inject(VirtualHostCookieService);
	private readonly ngZone = inject(NgZone);
	private readonly destroyRef = inject(DestroyRef);
	private readonly chain = inject(Chain);

	/**
	 * @description
	 * The Customer object from application state
	 */
	private readonly customer$ = this.store
		.select('CUSTOMER')
		.pipe(takeUntilDestroyed(this.destroyRef));

	protected readonly feedbackSurveyId = this.chain.feedbackSurveyId;
	readonly feedbackSurveyEnabled = this.ngrxStore.selectSignal(
		selectKillswitch('feedbackSurveyEnabled')
	);

	/**
	 * @description
	 * Flag to enable Bazaar Voice script loading using UTAG
	 */
	readonly isUtagBazaarvoiceEnabled = this.ngrxStore.selectSignal(
		selectKillswitch('isUtagBazaarvoiceEnabled')
	);

	readonly isPLAEnabled = this.ngrxStore.selectSignal(selectKillswitch('isPLAEnabled'));

	// These are used to prevent code from running multiple times -- not used in template
	private hasStoreInfo = false;
	private sddZipCode = '';

	/**
	 * @description
	 * The currently resolved SKU, used for Same Day Delivery info
	 */
	readonly resolvedSku = this.ngrxStore.selectSignal(selectResolvedSku);

	readonly dynamicTemplateName = this.ngrxStore.selectSignal(selectTemplateName);

	readonly adeptMindScriptEnabled = this.ngrxStore.selectSignal(
		selectKillswitch('adeptMindScriptEnabled')
	);

	readonly reviewsOff = this.ngrxStore.selectSignal(selectKillswitch('reviewsOff'));

	readonly sameDayDeliveryEnabled = this.ngrxStore.selectSignal(
		selectKillswitch('sameDayDeliveryEnabled')
	);

	/**
	 * @description Flag to enable Feedback after main content (CLS performance improvement)
	 */
	readonly enableFeedback = signal(false);

	/**
	 * @description
	 * replaces the isPlatformBrowser(this.platformId) function call
	 */
	readonly isPlatformBrowser = signal(isPlatformBrowser(this.platformId));

	readonly isPLARequest = computed(() => this.productService.isPLA(this.isPlatformBrowser()));
	readonly isPLA = computed(() => {
		const isISD = this.dynamicTemplateName() === DynamicProductTemplate.IN_STOCK_DEFAULT;
		return this.isPLAEnabled() && isISD && this.isPLARequest();
	});

	constructor() {
		const document = this.document;

		// Check authentication status and set up authentication status stream
		this.authService.localAuthSetup();

		// set dsg_vh in the common state
		this.ngrxStore.dispatch(setCommonData());
		this.ngrxStore.dispatch(setCommonDsgVhHeader({ dsgVh: this.cookieService.get('DSG_VH') }));

		// Reset the analytics
		if (!this.window.siteData) {
			this.analyticsService.resetAnalytics();
		}

		// Check to see if this has been defined
		if (typeof BUILD_VERSION === 'string' && BUILD_VERSION.trim() !== '') {
			// Set the build version on the global window
			this.window.BUILD_VERSION = BUILD_VERSION;
		}

		// Subscribe to the router events and filter to only `NavigationEnd`
		this.router.events
			.pipe(
				takeUntilDestroyed(this.destroyRef),
				filter((event) => event instanceof NavigationEnd),
				map(() => this.activatedRoute),
				map((route) => {
					while (route.firstChild) {
						route = route.firstChild;
					}
					return route;
				}),
				mergeMap(
					(route: ActivatedRoute) =>
						new Observable((observer) => {
							observer.next({
								data: route.data,
								params: route.params,
								queryParams: route.queryParams
							});
							observer.complete();
						})
				)
			)
			.subscribe((route: Pick<ActivatedRoute, 'data' | 'params' | 'queryParams'>) => {
				// Get a Promise for route.data
				const dataPromise = firstValueFrom(route.data);

				// Get a Promise for route.params
				const paramsPromise = firstValueFrom(route.params);

				// Get a Promise for route.queryParams
				const queryParamsPromise = firstValueFrom(route.queryParams);

				// Wait until all of the Promises have been resolved
				Promise.all([dataPromise, paramsPromise, queryParamsPromise]).then((data) => {
					// Get the current Product from the route data
					const CURRENT_PRODUCT = this.getCurrentProductFromRoute(
						data
					) as ProductDataForAnalytics;

					// Get the current query parameters from the route query params
					const CURRENT_QUERY_PARAMS = data[2];

					// Only execute in the Browser
					if (this.isPlatformBrowser()) {
						// Get the current Date/Time
						const CURRENT_DATETIME = new Date();

						// Set the chain
						this.analyticsService.setChain(this.chain.chainName);

						// Set the Error ID (if present)
						this.analyticsService.setErrorId();

						// Set the Error Message (if present)
						this.analyticsService.setErrorMessage();

						// Set the referring URL
						this.analyticsService.setReferringPageUrl(this.document.referrer);

						// Set the server date
						this.analyticsService.setServerDate(
							this.analyticsService.getFormattedDate(CURRENT_DATETIME)
						);

						// Set the server time
						this.analyticsService.setServerTime(
							this.analyticsService.getFormattedTime(CURRENT_DATETIME)
						);

						this.setAnalyticsTypeForProductPage(CURRENT_PRODUCT);

						// Get the Customer from state and populate related analytics
						this.customer$.subscribe((customer: Customer) => {
							// Set the Loyalty ID
							this.analyticsService.setLoyaltyID(customer.x_acNumber || '');

							// Set the LoyaltyBalance
							this.analyticsService.setLoyaltyPointsBalance(
								customer.x_acBalancePoints || ''
							);

							// Set the session ID
							this.analyticsService.setSessionID(customer.sessionId);

							// Set the user ID
							this.analyticsService.setUserID(customer.userId);

							// Set the visits count
							this.analyticsService.setVisitNumber(customer.visitCount);
						});

						// Check if Product data is available to set the Product related analytics and meta tags
						if (CURRENT_PRODUCT) {
							const shippingInfo: Shipping =
								this.productSelectionStateService.getShippingInfoData();

							this.setHomrSpecificData(CURRENT_PRODUCT, shippingInfo);

							// Set the Page property
							this.setAnalyticsPageForProduct(CURRENT_PRODUCT);

							this.setProductDataEventsObservableForAnalytics(
								CURRENT_PRODUCT,
								shippingInfo
							);

							// Set category affinity in Target
							this.setTargetCategoryAffinity(CURRENT_PRODUCT);

							this.enableBVOnDocumentReady();
						}

						// Set the base Search properties
						this.initializeSearchPageProperties(CURRENT_QUERY_PARAMS);

						// Check to see if the `param` query parameter is present
						this.setPromoIdCookie(CURRENT_QUERY_PARAMS);

						// PDP Adept Mind Script
						this.loadAdeptMindScriptIfEnabled(CURRENT_PRODUCT);
					} else {
						// Only execute on the Server
						/*
						 * Canonical link element
						 */
						// Canonical URL
						const canonicalUrl = `${this.chain.metaCanonical}${this.router.url.split('?')[0]}`;

						this.createCanonicalTag(canonicalUrl);
						/*
						 * OpenGraph url meta tag
						 */
						this.metaService.addTag(
							{ property: 'og:url', content: canonicalUrl },
							true
						);
					}
				});
			});

		/*
		 * Only execute on browser
		 */
		if (this.isPlatformBrowser()) {
			// eliminate 'persistent' click focus
			document.body.addEventListener('mousedown', () => {
				document.body.classList.add('mouse-Event');
			});

			document.body.addEventListener('keydown', () => {
				document.body.classList.remove('mouse-Event');
			});

			// Initialize the Cart Service
			this.cartService.initialize();

			// Check cookie and populate Store
			this.authService.checkIfReturningCustomer();

			// Set default store
			this.storeService.initialize();

			/*
			 * DSG_UID cookie
			 */
			if (!this.cookieService.check('DSG_UID')) {
				// Create a DSG_UID cookie
				this.cookieService.set(
					'DSG_UID',
					this.utilityService.generateUUID(),
					365,
					'/',
					'',
					''
				);
			}

			// Virtual Host chain identifier
			this.virtualHostCookieService.setVirtualHostCookie();
		}

		this.killSwitchService.getKillSwitchesEnvironment$().subscribe((data) => {
			this.store.set('ENVIRONMENT_KILLSWITCH_VALUES', data);
		});
	}

	private setAnalyticsTypeForProductPage(CURRENT_PRODUCT: ProductDataForAnalytics): void {
		if (CURRENT_PRODUCT) {
			this.analyticsService.setType('page', 'Product Details Page');
		}
	}

	private setAnalyticsPageForProduct(CURRENT_PRODUCT: ProductDataForAnalytics): void {
		if ('style' in CURRENT_PRODUCT) {
			this.analyticsService.setPage(
				`Product : ${CURRENT_PRODUCT.style.partNumber} : ${CURRENT_PRODUCT.style.name}`
			);
		} else {
			this.analyticsService.setPage(
				`Product : ${CURRENT_PRODUCT.partNumber} : ${CURRENT_PRODUCT.name}`
			);
		}
	}

	private loadAdeptMindScriptIfEnabled(CURRENT_PRODUCT: ProductDataForAnalytics): void {
		if (CURRENT_PRODUCT && this.adeptMindScriptEnabled()) {
			this.utilityService.loadExternalScript(adeptmindScriptPath);
		}
	}

	private setPromoIdCookie(CURRENT_QUERY_PARAMS: Params): void {
		if (
			typeof CURRENT_QUERY_PARAMS.param === 'string' &&
			CURRENT_QUERY_PARAMS.param.trim() !== '' &&
			!this.cookieService.check('promoId')
		) {
			// Set the promoId cookie
			this.cookieService.set('promoId', CURRENT_QUERY_PARAMS.param, '', '/', '', '');
		}
	}

	private initializeSearchPageProperties(CURRENT_QUERY_PARAMS: Params): void {
		this.analyticsService.setSearchPageBaseProperties({
			searchDetails: {
				DYMTerm: CURRENT_QUERY_PARAMS.dymterm || CURRENT_QUERY_PARAMS.DYMTerm || '',
				DYMType: CURRENT_QUERY_PARAMS.didyoumean || CURRENT_QUERY_PARAMS.didYouMean || '',
				replacementTerm: CURRENT_QUERY_PARAMS.replacementTerm || '',
				searchTerm:
					CURRENT_QUERY_PARAMS.enteredSearchTerm || CURRENT_QUERY_PARAMS.searchTerm || '',
				searchType: CURRENT_QUERY_PARAMS.searchType || ''
			}
		});
	}

	private getCurrentProductFromRoute(
		data: [Data, Params, Params]
	): ProductsData | BundleProductDataForAnalytics {
		const currentPath = this.httpService.getUrlPath();

		// Ensure the current path is a product page
		if (!currentPath?.startsWith('/p/')) {
			return undefined;
		}

		if (data[0].data) {
			const { bundleData, productsData } = data[0].data as ProductDetailsByUrlResponse;

			if (bundleData) {
				return {
					...bundleData,
					ecodes: productsData,
					pageTemplate: 'Bundle'
				};
			}

			return productsData[0];
		}

		return data[0].product;
	}

	ngOnInit(): void {
		// Enable Feedback and Footer after the main content is shown (CLS performance improvement)
		this.enableFeedbackAndFooter();

		this.ngrxStore.dispatch(
			modernCheckoutCookieChecked({
				value: this.cookieService.get('modern') === 'true' // string 'true' on purpose
			})
		);

		// Check if running in the browser
		if (!this.isPlatformBrowser()) {
			return;
		}

		if (!this.hasStoreInfo && typeof window.headerFunctions?.getStoreInfo === 'function') {
			const storeDetails = window.headerFunctions?.getStoreInfo();
			if (storeDetails?.detail) {
				const { detail } = storeDetails;

				this.ngrxStore.dispatch(setHeaderServiceStoreInfo({ storeInfo: detail }));

				if (detail.name) {
					this.ngrxStore.dispatch(setUserStoreName({ storeName: detail.name }));
				}
			}
		}

		if (!this.sddZipCode) {
			// Attempt to get the delivery zip code from cookies.
			const deliveryZip = this.headerLocationService.getDeliveryZipCookie();

			// If delivery zip code is not available, get the whereabouts zip code.
			const whereaboutsZip = this.headerLocationService.getWhereaboutsCookie();

			// Assign the first available zip code to sddZipCode.
			this.sddZipCode = deliveryZip || whereaboutsZip;

			// Dispatch the zip code to the store.
			this.ngrxStore.dispatch(setUserZipCode({ zipCode: this.sddZipCode }));
		}

		this.checkAndFetchSddInfo();
	}

	/**
	 * Check if we have a valid SKU and zip code and fetch SDD info if we do
	 */
	checkAndFetchSddInfo(): void {
		const currentSku = this.resolvedSku();
		if (currentSku?.partNumber && this.sddZipCode && this.sameDayDeliveryEnabled()) {
			this.ngrxStore.dispatch(
				fetchSDDInfo({
					zipCode: this.sddZipCode,
					skuId: Number(currentSku.partNumber)
				})
			);
		}
	}

	ngOnDestroy(): void {
		this.eventBusService.unsubscribe(Events.LoyaltyBalance);
	}

	private enableFeedbackAndFooter(): void {
		this.eventBusService.on(Events.EnableFooter, () => {
			this.enableFeedback.set(true);
			const footerElement = this.document.getElementById(
				this.environment.api.reactMarginals.footerHTMLId
			);
			if (footerElement) {
				this.renderer.setStyle(footerElement, 'display', 'block');
			}

			this.eventBusService.unsubscribe(Events.EnableFooter);
		});
	}

	/**
	 * @description
	 * Bazaar Voice Review and Q&A Logic
	 */
	private enableBV(): void {
		// Don't load if BV is already loaded
		if (this.window['$BV']) {
			return;
		}

		this.initializeBazaarVoice();
	}

	/**
	 * @description
	 * Initializes Bazaar Voice functionality
	 * @returns void
	 */
	private initializeBazaarVoice(): void {
		const isQnAEligible = this.chain.chainMarketingIdentifierAbbr === 'dsg';

		// Load the appropriate script based on Q&A eligibility
		if (isQnAEligible) {
			this.loadBVScript(true); // Load Q&A script
			this.eventBusService.emit(new EmitEvent(Events.QAndAEnabled, true));
		} else {
			this.loadBVScript(false); // Load standard script
			this.eventBusService.emit(new EmitEvent(Events.QAndAEnabled, false));
		}
	}

	/**
	 * @description
	 * Loads the appropriate Bazaarvoice script
	 * @param useQAScript Whether to load the Q&A enabled script
	 */
	private loadBVScript(useQAScript: boolean): void {
		const scriptKey = useQAScript
			? `${this.chain.chainMarketingIdentifierAbbr}QA`
			: this.chain.chainMarketingIdentifierAbbr;
		const scriptUrl = this.environment.thirdParty.bv.store[scriptKey].script;
		this.utilityService.loadExternalScript(scriptUrl);
	}

	/**
	 *
	 * @description Checks for the presence of descriptive attributes which qualify for a
	 * category affinity-based PZ experience in Adobe Target. If one is found, metadata
	 * from the attribute is passed to the analytics service which will ultimately send a
	 * category affinity value to Target after some further checks.
	 */
	private setTargetCategoryAffinity(currentProduct: ProductDataForAnalytics): void {
		const qualifyingAttribute = currentProduct?.style?.descriptiveAttributes?.find(
			(attr) => attr.identifier === '3445' || attr.identifier === '6702'
		);

		if (qualifyingAttribute) {
			this.analyticsService.triggerCategoryAffinityEvent(
				qualifyingAttribute.identifier,
				qualifyingAttribute.name,
				qualifyingAttribute.value
			);
		}
	}

	/**
	 * @description set homrPage name and add to window and set value for state
	 */
	private setHomrSpecificData(
		currentProduct: ProductDataForAnalytics,
		shippingInfo: Shipping
	): void {
		let homrPageName: string;
		// Check for LTL for dynamic ticker
		if (shippingInfo?.shippingClass?.indexOf('ltl') > -1) {
			// Set the homr page name to the LTL ticker name
			homrPageName = 'ltl-ticker';
		} else {
			// Set the homr page name to the current product eCode
			homrPageName =
				`${'style' in currentProduct ? currentProduct.style.partNumber : undefined}`.toLowerCase();
		}

		// Set the homR page name on the service
		this.homrDataService.setPageName(homrPageName);

		// Resolve the homR name Promise
		this.window.homrNamePromiseResolve(homrPageName);
	}

	/**
	 * @description connect with all state data needed for analytics events. Once all data is received make analytics data available in the window
	 */
	private setProductDataEventsObservableForAnalytics(
		currentProduct: ProductDataForAnalytics,
		shippingInfo: Shipping
	): void {
		// Async Events observable for setting product data in data layer
		const productDataEventsObservable = new Subject();

		// The async events to count for setting product data in data layer
		const productDataEventsToCount = [];

		// Inventory map for BOPIS for analytics
		let inventoryMapBOPIS: ISkuLocation[];

		// Inventory map for eComm for analytics
		let inventoryMapEcomm: ISkuLocation[];

		// LoyaltyBalance on page load
		let loyaltyBalance: LoyaltyBalance;

		this.ngrxStore
			.select(selectPromotionData)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((promotionData) => {
				if (promotionData) {
					// Current promotions
					const currentPromotions: AnalyticsPromotion[] = [];

					// Check for ProductLevelPromotion data
					if (typeof promotionData.ProductLevelPromotion !== 'undefined') {
						// Add the promotion to the Array
						currentPromotions.push({
							type: 'ProductLevelPromotion',
							desc: promotionData.ProductLevelPromotion.pdpDesc,
							promotionId: (promotionData.ProductLevelPromotion as PromotionDetailsV1)
								.promotionId
						});
					}

					// Check for ShippingPromotion data
					if (typeof promotionData.ShippingPromotion !== 'undefined') {
						// Add the promotion to the Array
						currentPromotions.push({
							type: 'ShippingPromotion',
							desc: promotionData.ShippingPromotion.pdpDesc,
							promotionId: (promotionData.ShippingPromotion as PromotionDetailsV1)
								.promotionId
						});
					}

					// Set promotions on the data layer
					this.analyticsService.setPromotionMessages(currentPromotions);

					// Add incoming event to the Array
					productDataEventsToCount.push(Events.PromoEvent);

					// Publish the event to the Subscription
					productDataEventsObservable.next(Events.PromoEvent);
				}
			});

		this.ngrxStore
			.select(selectEcommInventory)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((ecommInventoryData) => {
				if (ecommInventoryData) {
					// Set the inventory map
					inventoryMapEcomm = ecommInventoryData;

					// Add incoming event to the Array
					productDataEventsToCount.push(Events.OnlineInventoryResults);

					// Publish the event to the Subscription
					productDataEventsObservable.next(Events.OnlineInventoryResults);
				}
			});

		this.ngrxStore
			.select(selectBopisInventory)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((storeInventoryData) => {
				if (storeInventoryData) {
					// Set the inventory map
					inventoryMapBOPIS = storeInventoryData;

					// Add incoming event to the Array
					productDataEventsToCount.push(Events.StoreInventoryResults);

					// Publish the event to the Subscription
					productDataEventsObservable.next(Events.StoreInventoryResults);
				}
			});

		// Listen for the LoyaltyBalance Event
		this.eventBusService.on(Events.LoyaltyBalance, (eventData: LoyaltyBalance) => {
			// Check to see if the Event has already been triggered
			if (
				Array.isArray(productDataEventsToCount) &&
				productDataEventsToCount.indexOf(Events.LoyaltyBalance) === -1
			) {
				// Set loyaltyBalance
				loyaltyBalance = eventData;

				// Add incoming event to the Array
				productDataEventsToCount.push(Events.LoyaltyBalance);

				// Publish the event to the Subscription
				productDataEventsObservable.next(Events.LoyaltyBalance);
			}
		});

		// The async events to observe for setting product data in data layer
		let productDataEventsToObserve = [Events.PromoEvent, Events.LoyaltyBalance];
		if ('style' in currentProduct && currentProduct.style) {
			productDataEventsToObserve = [
				Events.OnlineInventoryResults,
				Events.PromoEvent,
				Events.StoreInventoryResults,
				Events.LoyaltyBalance
			];
		}

		// Observe all events passed in to determine if we can trigger analytics
		const productDataEventsSubscription = productDataEventsObservable
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe(() => {
				// Count of matches
				let matchCount = 0;

				// Iterate the required events to match
				productDataEventsToObserve.forEach((eventType) => {
					// Check to see if the current event has been triggered
					if (productDataEventsToCount.indexOf(eventType) > -1) {
						// Match found, increment counter
						matchCount += 1;
					}
				});

				// Early return if we haven't matched all required events yet
				if (matchCount !== productDataEventsToObserve.length) {
					return;
				}

				// Unsubscribe since we have all the data we need
				productDataEventsSubscription.unsubscribe();

				const analyticsColorBuckets =
					'style' in currentProduct
						? this.productSelectionService.setAnalyticsBuckets(
								currentProduct.style,
								currentProduct.skus
							)
						: undefined;

				if (this.isPLA()) {
					currentProduct.pageTemplate = 'PLA';
				}

				if (
					(!('pageTemplate' in currentProduct) || !currentProduct?.pageTemplate) &&
					(!('skus' in currentProduct) ||
						!currentProduct?.skus ||
						currentProduct?.skus?.length === 0)
				) {
					currentProduct.pageTemplate = 'OutOfStock';
				}

				// Set the Product page specific analytics
				this.analyticsService.setProductPageDetailsProperties(
					currentProduct,
					analyticsColorBuckets,
					inventoryMapEcomm,
					inventoryMapBOPIS,
					loyaltyBalance,
					shippingInfo
				);

				/*
				 * Only execute on browser
				 */
				if (this.isPlatformBrowser()) {
					// Avoid including app-stability delaying activities from utag in angular's zone
					this.ngZone.runOutsideAngular(() => {
						// Trigger analytics `NewPageView` event
						this.analyticsService.triggerAnalyticsEvent('NewPageView');
					});
				}
			});
	}

	createCanonicalTag(canonicalUrl: string): void {
		// Create a link element
		const canonicalLink = this.document.createElement('link');

		// Set the link relationship attribute
		canonicalLink.setAttribute('rel', 'canonical');

		// Set the link href attribute
		canonicalLink.setAttribute('href', canonicalUrl);

		// Append the link element to the head element
		this.document.head.appendChild(canonicalLink);
	}

	private enableBVOnDocumentReady(): void {
		if (!this.isUtagBazaarvoiceEnabled() && !this.reviewsOff()) {
			// Defer loading BV until the document is ready
			this.utilityService.deferFunctionToDocumentReady(() => {
				//  Enable Bazaar Voice Javascript for Q&A and Reviews if not a secure launch
				this.enableBV();
			});
		}
	}

	/**
	 * @description use the header service to get athlete's store information on page load
	 */
	setStoreDetails(event: CustomEvent<HeaderStoreSelection>) {
		this.hasStoreInfo = true;

		this.updateStoreInfo(event);
	}

	/**
	 * @description Listen for any changes in the header service to the store selection
	 * if there is a change update the state
	 */
	updateStoreDetails(event: CustomEvent<HeaderStoreSelection>) {
		this.updateStoreInfo(event);
	}

	private updateStoreInfo(event: CustomEvent<HeaderStoreSelection>) {
		if (event?.detail) {
			this.ngrxStore.dispatch(changeHeaderServiceStoreInfo({ storeInfo: event.detail }));
			if (event.detail?.name) {
				this.ngrxStore.dispatch(setUserStoreName({ storeName: event.detail.name }));
			}
		}
	}

	/**
	 * @description Listen for any changes in the header service to the store selection
	 * or use the header service to get athlete's delivery zip code on page load.
	 * If there is a change, update the state.
	 */
	handleDeliveryZip(event: CustomEvent<string>) {
		if (event?.detail && event.detail !== this.sddZipCode) {
			this.sddZipCode = event.detail;
			this.ngrxStore.dispatch(setUserZipCode({ zipCode: event.detail }));

			// If we have a resolved SKU, also dispatch fetchSDDInfo
			const currentSku = this.resolvedSku();
			if (currentSku?.partNumber && this.sameDayDeliveryEnabled()) {
				this.ngrxStore.dispatch(
					fetchSDDInfo({
						zipCode: event.detail,
						skuId: Number(currentSku.partNumber)
					})
				);
			}
		}
	}

	handleWindowFlagsEvent() {
		this.killSwitchService
			.getAllKillSwitchesForCurrentChain$()
			.pipe(take(1))
			.subscribe((killswitches) => {
				this.ngrxStore.dispatch(windowFlagsEventKillswitchesSet({ killswitches }));
			});
	}
}
